import { memo, useState, useRef, useCallback } from "react";
import { NumericFormat } from "react-number-format";
import {
  TextFieldComponent,
  TextAreaComponent,
  AutocompleteComponent,
  ButtonComponent,
  SpinnerComponent,
} from "@zenith/components";
import { FormDataType, FormErrorsType } from "../types";
import { MAX_BENEFICIARY_ACCOUNT_NUMBER_LENGTH } from "@zenith/data/constants";
import { AutocompleteSelectItemsType } from "@zenith/components/ui/AutocompleteComponent/autocomplete-component/interfaces";
import IconButton from "@mui/joy/IconButton";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { UserSquare } from "@zenith/assets/icons/components";
import BeneficiaryList from "./BeneficiaryList";
import { sortedBeneficiaryDetailsRecord } from "@zenith/pages/BeneficiariesPage/beneficiaries-page/beneficiaries-page";
import { FormGroup } from "@mui/material";
import { CheckBox } from "@zenith/components/ui/CheckBoxComponent/checkbox-component";

interface WalletTransferFormProps {
  formData: FormDataType;
  formErrors: FormErrorsType;
  categoryList: AutocompleteSelectItemsType[];
  availableLimit: number;
  availableBalance: number;
  isLoading: boolean;
  onSubmit: () => void;
  onFormDataChange: (field: keyof FormDataType, value: string | number) => void;
  validateAccountNumber?: (accountNumber: string) => Promise<boolean>;
  validateAmount?: (amount: number) => void;
  onShowBeneficiaryList: () => void;
  showBeneficiaryList: boolean;
  beneficiaryList?: sortedBeneficiaryDetailsRecord[];
  selectedBeneficiary?: sortedBeneficiaryDetailsRecord;
  isLoadingBeneficiaries: boolean;
  beneficiariesError: boolean;
  saveBeneficiary: boolean;
  onBeneficiarySelect: (beneficiary: sortedBeneficiaryDetailsRecord) => void;
  onSaveBeneficiaryChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isValidating?: boolean;
}

const WalletTransferForm = memo(function WalletTransferForm({
  formData,
  formErrors,
  categoryList,
  availableLimit,
  availableBalance,
  isLoading,
  onSubmit,
  onFormDataChange,
  validateAccountNumber,
  validateAmount,
  showBeneficiaryList,
  isLoadingBeneficiaries,
  beneficiariesError,
  beneficiaryList,
  selectedBeneficiary,
  onShowBeneficiaryList,
  onBeneficiarySelect,
  saveBeneficiary,
  onSaveBeneficiaryChange,
  isValidating = false,
}: WalletTransferFormProps) {
  const [isBeneficiarySaved, setIsBeneficiarySaved] = useState(saveBeneficiary);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.replace(/[^\d.]/g, "");
    const numericValue = parseFloat(rawValue) || 0;
    onFormDataChange("amount", numericValue);
    if (validateAmount) {
      validateAmount(numericValue);
    }
  };

  const handleAccountNumberChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      onFormDataChange("beneficiaryAccountNumber", value);
    },
    [onFormDataChange]
  );

  const handleCategoryChange = (value: string, label: string) => {
    onFormDataChange("categoryId", value);
    onFormDataChange("categoryName", label);
  };

  const handleCategoryInputChange = (value: string) => {
    onFormDataChange("categoryName", value);
    if (formData.categoryId) onFormDataChange("categoryId", "");
  };

  const isSubmitDisabled = () => {
    return (
      !formData.amount ||
      formData.beneficiaryAccountNumber.length !==
        MAX_BENEFICIARY_ACCOUNT_NUMBER_LENGTH ||
      !formData.beneficiaryName ||
      isValidating ||
      formErrors.amount.hasError ||
      formErrors.beneficiaryAccountNumber.hasError
    );
  };

  const handleBeneficiarySelect = (
    beneficiary: sortedBeneficiaryDetailsRecord
  ) => {
    onBeneficiarySelect(beneficiary);
    setIsBeneficiarySaved(true);
  };

  const handleClickAway = () => {
    if (showBeneficiaryList) {
      onShowBeneficiaryList();
    }
  };

  const showValidatingIndicator =
    isValidating &&
    formData.beneficiaryAccountNumber.length === 10 &&
    !formData.beneficiaryName;

  return (
    <>
      <div className="mb-8 flex flex-col gap-4">
        <div className="relative" ref={containerRef}>
          <ClickAwayListener onClickAway={handleClickAway}>
            <div>
              <TextFieldComponent
                id="finopay-account-number-field"
                label="Finopay Account Number"
                labelId="finopay-account-number-field-label"
                placeholder="Enter Finopay account number"
                type="tel"
                className="border-neutral-300 pr-4 pl-4 shadow-none"
                value={formData.beneficiaryAccountNumber}
                hasError={formErrors.beneficiaryAccountNumber.hasError}
                validationMessage={formErrors.beneficiaryAccountNumber.message}
                maxLength={10}
                onChange={handleAccountNumberChange}
                endDecorator={
                  <div className="flex items-center gap-2">
                    {showValidatingIndicator && (
                      <SpinnerComponent
                        size={20}
                        type="primary"
                        showIcon={false}
                      />
                    )}
                    <IconButton
                      color="neutral"
                      onClick={onShowBeneficiaryList}
                    >
                      <UserSquare width={24} height={24} />
                    </IconButton>
                  </div>
                }
              />
              {showBeneficiaryList && (
                <div className="absolute z-[1300] w-full rounded-b-xl border border-t-0 border-neutral-300 bg-white p-3 pt-0">
                  <hr />
                  <BeneficiaryList
                    isLoading={isLoadingBeneficiaries}
                    hasError={beneficiariesError}
                    beneficiaries={beneficiaryList}
                    selectedBeneficiary={selectedBeneficiary}
                    onSelect={handleBeneficiarySelect}
                  />
                </div>
              )}
            </div>
          </ClickAwayListener>
        </div>
        <div>
          <TextFieldComponent
            id="finopay-beneficiary-name-field"
            label="Beneficiary Name"
            labelId="finopay-beneficiary-name-field-label"
            className="h-12 disabled:text-neutral-600"
            placeholder="Beneficiary name will appear here"
            type="text"
            value={formData.beneficiaryName}
            disabled
            disabledTextColor="#626262"
            maxLength={50}
            onChange={(_: React.ChangeEvent<HTMLInputElement>) => void 0}
          />
        </div>
        <FormGroup className="w-max">
          <CheckBox
            variant="triState"
            label="Save Beneficiary"
            state={isBeneficiarySaved ? "checked" : "unchecked"}
            onChange={(nextState: string) => {
              const checked = nextState === "checked";
              setIsBeneficiarySaved(checked);
              onSaveBeneficiaryChange({
                target: { checked },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
          />
        </FormGroup>
      </div>

      <hr className="bg-neutral-600" />

      <h2 className="mt-6 mb-4 text-xl font-medium text-neutral-900">
        Transfer Details
      </h2>

      <div className="mb-4 flex flex-col gap-4">
        <div>
          <div className="ml-2 mb-1 text-sm text-neutral-500">Amount</div>
          <div className="relative">
            <NumericFormat
              allowNegative={false}
              placeholder="0.00"
              value={formData.amount || ""}
              id="transfer-details-amount-input"
              className={`h-12 w-full rounded-1.5lg border px-4 py-3 pl-11 text-lg leading-normal text-black ${
                formErrors.amount.hasError ? "border-red-500" : ""
              }`}
              decimalScale={2}
              fixedDecimalScale
              onChange={handleAmountChange}
              allowLeadingZeros={false}
              thousandSeparator={true}
              valueIsNumericString
              isAllowed={(values) => {
                const { floatValue } = values;
                return (
                  floatValue === undefined || floatValue.toString().length <= 16
                );
              }}
            />
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg">
              ₦
            </span>
          </div>
          {formErrors.amount.hasError && (
            <div className="mx-1.5 mt-1 text-sm text-red-400">
              {formErrors.amount.message}
            </div>
          )}
        </div>

        <AutocompleteComponent
          id="send-money-category"
          label="Category (optional)"
          labelId="category-dropdown-label"
          className="w-full"
          options={categoryList || []}
          onChange={handleCategoryChange}
          onInputChange={handleCategoryInputChange}
          placeholder="Select a category"
          selectValue={
            formData.categoryId && formData.categoryName
              ? { label: formData.categoryName, value: formData.categoryId }
              : null
          }
          showSearchIcon
        />

        <TextAreaComponent
          minRows={3}
          id="send-money-textarea"
          labelId="send-money-textarea-label"
          label="Description (optional)"
          placeholder="Enter a description with a maximum of 50 characters"
          value={formData.description}
          onChange={(e) => onFormDataChange("description", e.target.value)}
          maxLength={50}
        />
      </div>

      <div className="mt-3 flex w-full justify-center">
        <ButtonComponent
          label="Continue"
          onClick={onSubmit}
          disabled={isSubmitDisabled() || isLoading}
          isLoading={isLoading}
          size="large"
          type="primary"
          id="send-money-button"
          fullWidth
        />
      </div>
    </>
  );
});

export default WalletTransferForm;
